import { useEffect, useState } from "react";
import axios from "axios";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Loader2, Star, IndianRupee, UserCheck } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const schema = z.object({
  title: z.string().min(2, "Title is required"),
  company_name: z.string().optional(),
  department: z.string().min(2, "Department is required"),
  industry: z.string().optional(),
  location: z.string().min(2, "Location is required"),
  employment_type: z.string().min(2, "Type is required"),
  experience: z.string().min(1, "Experience required"),
  qualification: z.string().optional(),
  shift: z.string().optional(),
  salary_range: z.string().optional(),
  salary_min: z.coerce.number().optional(),
  salary_max: z.coerce.number().optional(),
  description: z.string().min(10, "Add a meaningful description"),
  responsibilities: z.string().optional(),
  requirements: z.string().optional(),
  skills: z.string().optional(),
  benefits: z.string().optional(),
  openings: z.coerce.number().min(1).max(999),
  category: z.string().default("general"),
  is_featured: z.boolean().default(false),
  is_active: z.boolean().default(true),
  expires_at: z.string().optional(),  // YYYY-MM-DD
  commission_amount: z.union([z.coerce.number().min(0), z.literal("")]).optional(),
});

const EMPTY = {
  title: "", company_name: "", department: "", industry: "", location: "",
  employment_type: "Permanent", experience: "", qualification: "", shift: "",
  salary_range: "", salary_min: undefined, salary_max: undefined,
  description: "", responsibilities: "", requirements: "", skills: "", benefits: "",
  openings: 1, category: "general", is_featured: false, is_active: true, expires_at: "",
  commission_amount: "",
};

const CATEGORIES = [
  { value: "general", label: "General" },
  { value: "hot", label: "Hot Job" },
  { value: "walkin", label: "Walk-in" },
  { value: "fresher", label: "Freshers" },
];

const TYPES = ["Permanent", "Contract", "Temporary", "Full-time", "Part-time", "Internship"];

function listToText(v) { return Array.isArray(v) ? v.join("\n") : (v || ""); }
function csvToText(v) { return Array.isArray(v) ? v.join(", ") : (v || ""); }
function isoToDate(v) {
  if (!v) return "";
  try { return new Date(v).toISOString().slice(0, 10); } catch { return ""; }
}

function toFormDefaults(job) {
  if (!job) return EMPTY;
  return {
    ...EMPTY,
    ...job,
    responsibilities: listToText(job.responsibilities),
    requirements: listToText(job.requirements),
    benefits: listToText(job.benefits),
    skills: csvToText(job.skills),
    openings: job.openings ?? 1,
    is_active: job.is_active ?? true,
    is_featured: !!job.is_featured,
    category: job.category || "general",
    salary_min: job.salary_min ?? "",
    salary_max: job.salary_max ?? "",
    commission_amount: job.commission_amount ?? "",
    expires_at: isoToDate(job.expires_at),
  };
}

export default function JobFormDialog({ open, onOpenChange, job, onSaved }) {
  const editing = !!job?.id;
  const [freelancers, setFreelancers] = useState([]);
  const [assigned, setAssigned] = useState(new Set());   // ids selected
  const [initialAssigned, setInitialAssigned] = useState(new Set());
  const {
    register, handleSubmit, reset, watch, setValue, formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: toFormDefaults(job),
  });

  useEffect(() => { reset(toFormDefaults(job)); }, [job, reset]);

  // Load active freelancers + current assignments (edit mode)
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/admin/users?role=freelancer`)
      .then((r) => setFreelancers((r.data || []).filter((u) => u.is_active !== false)))
      .catch(() => setFreelancers([]));
    if (editing) {
      axios.get(`${API}/admin/jobs/${job.id}/assignments`)
        .then((r) => {
          const ids = new Set((r.data || []).map((u) => u.id));
          setAssigned(ids);
          setInitialAssigned(new Set(ids));
        })
        .catch(() => { setAssigned(new Set()); setInitialAssigned(new Set()); });
    } else {
      setAssigned(new Set());
      setInitialAssigned(new Set());
    }
  }, [open, editing, job]);

  const toggleAssigned = (id) => {
    setAssigned((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const onSubmit = async (values) => {
    const linesToArr = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);
    const csvToArr = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);
    const payload = {
      ...values,
      openings: Number(values.openings) || 1,
      responsibilities: linesToArr(values.responsibilities),
      requirements: linesToArr(values.requirements),
      benefits: linesToArr(values.benefits),
      skills: csvToArr(values.skills),
      salary_min: values.salary_min !== "" && values.salary_min !== null ? Number(values.salary_min) : null,
      salary_max: values.salary_max !== "" && values.salary_max !== null ? Number(values.salary_max) : null,
      commission_amount: values.commission_amount !== "" && values.commission_amount !== null ? Number(values.commission_amount) : null,
      expires_at: values.expires_at ? new Date(values.expires_at + "T23:59:59Z").toISOString() : null,
    };
    try {
      let jobId;
      if (editing) {
        await axios.put(`${API}/admin/jobs/${job.id}`, payload);
        jobId = job.id;
        toast.success("Job updated");
      } else {
        const { data } = await axios.post(`${API}/admin/jobs`, payload);
        jobId = data.id;
        toast.success("Job posted");
      }
      // Sync freelancer assignments
      const toAdd = [...assigned].filter((id) => !initialAssigned.has(id));
      const toRemove = [...initialAssigned].filter((id) => !assigned.has(id));
      if (toAdd.length > 0) {
        await axios.post(`${API}/admin/jobs/${jobId}/assign`, { freelancer_ids: toAdd });
      }
      for (const fid of toRemove) {
        try { await axios.delete(`${API}/admin/jobs/${jobId}/assign/${fid}`); } catch { /* ignore */ }
      }
      onSaved?.();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Could not save job");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="job-form-dialog" className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{editing ? "Edit Job" : "Post a Job"}</DialogTitle>
          <DialogDescription>
            {editing ? "Update opening details." : "Fields marked * are required. Jobs marked 'Featured' show on the homepage with a badge."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Top toggles */}
          <div className="flex flex-wrap items-center gap-6 p-4 rounded-xl bg-slate-50 border border-slate-200">
            <ToggleRow
              label={watch("is_active") ? "Published (visible on Careers page)" : "Draft (hidden)"}
              value={!!watch("is_active")}
              onChange={(v) => setValue("is_active", v)}
              testId="job-form-active"
            />
            <ToggleRow
              label={watch("is_featured") ? "Featured (homepage spotlight)" : "Regular"}
              value={!!watch("is_featured")}
              onChange={(v) => setValue("is_featured", v)}
              testId="job-form-featured"
              icon={<Star className="w-3.5 h-3.5 text-primary" />}
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Title *" error={errors.title?.message}>
              <Input data-testid="job-form-title" placeholder="e.g. Production Supervisor" {...register("title")} />
            </Field>
            <Field label="Company Name (optional)">
              <Input data-testid="job-form-company" placeholder="e.g. Confidential Client" {...register("company_name")} />
            </Field>
            <Field label="Department *" error={errors.department?.message}>
              <Input data-testid="job-form-dept" placeholder="e.g. Manufacturing" {...register("department")} />
            </Field>
            <Field label="Industry">
              <Input data-testid="job-form-industry" placeholder="e.g. Auto Components" {...register("industry")} />
            </Field>
            <Field label="Location *" error={errors.location?.message}>
              <Input data-testid="job-form-loc" placeholder="e.g. Bhiwandi, Maharashtra" {...register("location")} />
            </Field>
            <Field label="Employment Type *" error={errors.employment_type?.message}>
              <Select value={watch("employment_type")} onValueChange={(v) => setValue("employment_type", v, { shouldValidate: true })}>
                <SelectTrigger data-testid="job-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Experience *" error={errors.experience?.message}>
              <Input data-testid="job-form-exp" placeholder="e.g. 2-4 years" {...register("experience")} />
            </Field>
            <Field label="Qualification">
              <Input data-testid="job-form-qual" placeholder="e.g. B.E. Mechanical" {...register("qualification")} />
            </Field>
            <Field label="Shift">
              <Input data-testid="job-form-shift" placeholder="e.g. Day / Rotational" {...register("shift")} />
            </Field>
            <Field label="Salary Range (display)">
              <Input data-testid="job-form-salary" placeholder="e.g. ₹3 - 5 LPA" {...register("salary_range")} />
            </Field>
            <Field label="Salary Min (LPA — for filters)">
              <Input data-testid="job-form-salary-min" type="number" step="0.1" {...register("salary_min")} />
            </Field>
            <Field label="Salary Max (LPA — for filters)">
              <Input data-testid="job-form-salary-max" type="number" step="0.1" {...register("salary_max")} />
            </Field>
            <Field label="Openings *" error={errors.openings?.message}>
              <Input data-testid="job-form-openings" type="number" min={1} {...register("openings")} />
            </Field>
            <Field label="Category">
              <Select value={watch("category")} onValueChange={(v) => setValue("category", v)}>
                <SelectTrigger data-testid="job-form-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Expires On (auto-hide after this date)">
              <Input data-testid="job-form-expires" type="date" {...register("expires_at")} />
            </Field>
            <Field label="Freelancer Commission (₹)">
              <div className="relative">
                <IndianRupee className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                <Input data-testid="job-form-commission" type="number" min={0} step={100} className="pl-8" placeholder="e.g. 5000" {...register("commission_amount")} />
              </div>
            </Field>
          </div>

          <Field label="Assigned Freelancers">
            <div data-testid="job-form-assign" className="border border-slate-200 rounded-lg bg-white p-3 max-h-52 overflow-y-auto">
              {freelancers.length === 0 ? (
                <p className="text-xs text-slate-500 py-2 px-1">No active freelancers. Add one from the Freelancers tab.</p>
              ) : (
                <div className="grid sm:grid-cols-2 gap-2">
                  {freelancers.map((f) => (
                    <label
                      key={f.id}
                      data-testid={`job-form-assign-row-${f.id}`}
                      className={`flex items-center gap-2.5 px-3 py-2 rounded-md text-sm cursor-pointer border transition-colors ${
                        assigned.has(f.id)
                          ? "border-primary/40 bg-primary/5 text-slate-900"
                          : "border-slate-200 bg-white hover:bg-slate-50 text-slate-700"
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="rounded"
                        checked={assigned.has(f.id)}
                        onChange={() => toggleAssigned(f.id)}
                      />
                      <UserCheck className="w-3.5 h-3.5 text-primary shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">{f.name}</div>
                        <div className="text-[11px] text-slate-500 truncate">{f.mobile || f.email}</div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </Field>

          <Field label="Description *" error={errors.description?.message}>
            <Textarea data-testid="job-form-desc" rows={3} placeholder="Summarize the role…" {...register("description")} />
          </Field>
          <Field label="Responsibilities (one per line)">
            <Textarea data-testid="job-form-resps" rows={4} placeholder={"e.g. Manage daily operations\nLead a team of 10+"} {...register("responsibilities")} />
          </Field>
          <Field label="Requirements (one per line)">
            <Textarea data-testid="job-form-reqs" rows={3} placeholder={"e.g. Diploma in Engineering\nExperience in Tier-1 manufacturing"} {...register("requirements")} />
          </Field>
          <Field label="Skills (comma separated)">
            <Input data-testid="job-form-skills" placeholder="e.g. Lean, 5S, Team Leadership" {...register("skills")} />
          </Field>
          <Field label="Benefits (one per line)">
            <Textarea data-testid="job-form-benefits" rows={3} placeholder={"e.g. PF & ESIC\nHealth Insurance\nPerformance Bonus"} {...register("benefits")} />
          </Field>

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={isSubmitting} data-testid="job-form-submit" className="bg-primary hover:bg-primary-hover text-white">
              {isSubmitting ? <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Saving…</span> : (editing ? "Save changes" : "Post Job")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ToggleRow({ label, value, onChange, testId, icon }) {
  return (
    <label className="inline-flex items-center gap-3 cursor-pointer">
      <Switch checked={value} onCheckedChange={onChange} data-testid={testId} />
      <span className="text-sm text-slate-700 inline-flex items-center gap-1.5">{icon}{label}</span>
    </label>
  );
}

function Field({ label, error, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">{label}</Label>
      {children}
      {error && <p role="alert" className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
